import { useEffect } from 'react';

import { type FlagsContext, useApp, useFeatureFlags } from '@/contexts';
import { useAuth } from '@/libs';
import { trackDatadogError } from '@/libs/datadog';

import { buildFlagsContext } from './utils';

/**
 * Keeps the LaunchDarkly evaluation context in step with who is signed in and
 * which scope they are working in, and reports to the feature-flags context
 * when the first evaluation has settled.
 *
 * Mounted once, app-wide: a flag-gated route holds its redirect until this
 * reports in, so it must not be possible to render such a route without it.
 */
const LaunchDarklyUserIdentifier = () => {
  const {
    selectedRegion,
    selectedRegionName,
    selectedCropSeason,
    selectedCropSeasonName
  } = useApp();
  const { user } = useAuth();
  const { ldClient, identificationContextKey, settleIdentification } =
    useFeatureFlags();

  useEffect(() => {
    if (!user || !ldClient || !identificationContextKey) {
      return;
    }

    // Rebuilt whole rather than merged onto `ldClient.getContext()`: every
    // input is already in hand here, and identifying with a partial context —
    // even momentarily — re-evaluates every flag targeted at the kinds left
    // out, which is enough to bounce a user off a route gated on one.
    const context: FlagsContext = buildFlagsContext({
      user,
      selectedRegion,
      selectedRegionName,
      selectedCropSeason,
      selectedCropSeasonName
    });

    ldClient
      .identify(context)
      // Reported against the key captured here, so a completion that lands
      // after the user or scope moved on cannot vouch for the new context.
      .then(() => settleIdentification(identificationContextKey, 'evaluated'))
      // A failure is reported too, so a consumer gated on this reaches a
      // decision rather than waiting forever — but as `unevaluated`, because
      // the flags still describe an earlier context and must not be read as
      // this one's answer.
      .catch(error => {
        console.error(error);
        // Reported because the consequence is now user-visible and otherwise
        // untraceable: the route gate denies on `unevaluated`, so this is why
        // a user with the flag on lands on Home instead of the page.
        trackDatadogError(error, { feature: 'launchdarkly-identification' });
        settleIdentification(identificationContextKey, 'unevaluated');
      });
  }, [
    user,
    ldClient,
    identificationContextKey,
    selectedRegion,
    selectedRegionName,
    selectedCropSeason,
    selectedCropSeasonName,
    settleIdentification
  ]);

  return null;
};

export default LaunchDarklyUserIdentifier;
